// AB#2130 — layout route for /tenants/$slug. Owns the shared ['tenant', slug] query (children that
// need the tenant object re-issue the same key and dedupe against the app-global cache), the
// breadcrumb, the header card with its status / complete-setup mutations, and the tab strip; each
// tab renders into the <Outlet /> as a nested child route (see router.tsx).
import { Link, Outlet, useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'
import type { CompleteSetupResponse } from '@marque/api/central'
import { StatusBadge } from '../../components/tenant-detail/StatusBadge'

function tenantPortalUrl(slug: string) {
    const host = window.location.hostname.replace(/^central\./, '')
    return `https://${slug}.${host}`
}

// Tab link styling copied from components/Sidebar.tsx so the strip matches the app chrome.
const tabLinkCls = 'block px-3 py-2 rounded-md text-sm transition-colors'
const tabActiveProps = { className: 'bg-neutral-100 text-neutral-900 font-medium' }
const tabInactiveProps = { className: 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900' }

export function TenantDetailLayout() {
    const { slug } = useParams({ from: '/tenants/$slug' })
    const queryClient = useQueryClient()

    const { data: tenant, isLoading, isError } = useQuery({
        queryKey: ['tenant', slug],
        queryFn: () => centralApi.getTenant(slug),
    })

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenant', slug] })

    const statusMutation = useMutation({
        mutationFn: (status: string) => centralApi.changeTenantStatus(slug, status),
        onSuccess: invalidate,
    })

    const completeSetupMutation = useMutation({
        mutationFn: (): Promise<CompleteSetupResponse> => centralApi.completeSetup(slug),
        onSuccess: invalidate,
    })

    if (isLoading) return <p className="text-neutral-500 text-sm">Loading…</p>
    if (isError || !tenant) return <p className="text-red-600 text-sm">Failed to load tenant.</p>

    const nextStatus = tenant.status === 'Active' ? 'Suspended' : 'Active'
    const canChangeStatus = tenant.status === 'Active' || tenant.status === 'Suspended'

    return (
        <div className="space-y-8">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-neutral-500">
                <Link to="/tenants" className="hover:text-neutral-900">Tenants</Link>
                <span>/</span>
                <span className="text-neutral-900">{tenant.displayName}</span>
            </div>

            {/* Header card */}
            <div className="bg-white rounded-lg border border-neutral-200 p-6">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-neutral-900 flex items-center gap-3">
                            {tenant.displayName}
                            <StatusBadge status={tenant.status} />
                            {!tenant.isSetupComplete && (
                                <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                    Setup pending
                                </span>
                            )}
                        </h1>
                        <p className="text-sm text-neutral-500 mt-1 font-mono">{tenant.slug}</p>
                        <a
                            href={tenantPortalUrl(tenant.slug)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 mt-1 inline-flex items-center gap-1"
                        >
                            {tenantPortalUrl(tenant.slug)}
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 shrink-0" aria-hidden>
                                <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Zm6.75-3a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0V3.56l-3.97 3.97a.75.75 0 0 1-1.06-1.06l3.97-3.97h-2.69a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                            </svg>
                        </a>
                    </div>
                    <div className="flex items-center gap-2">
                        {!tenant.isSetupComplete && (
                            <button
                                onClick={() => completeSetupMutation.mutate()}
                                disabled={completeSetupMutation.isPending}
                                className="px-3 py-1.5 text-sm rounded-md font-medium transition-colors disabled:opacity-50 bg-orange-50 text-orange-800 hover:bg-orange-100 border border-orange-200"
                            >
                                {completeSetupMutation.isPending ? '…' : 'Mark Setup Complete'}
                            </button>
                        )}
                        {canChangeStatus && (
                            <button
                                onClick={() => statusMutation.mutate(nextStatus)}
                                disabled={statusMutation.isPending}
                                className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors disabled:opacity-50 ${
                                    nextStatus === 'Suspended'
                                        ? 'bg-yellow-50 text-yellow-800 hover:bg-yellow-100 border border-yellow-200'
                                        : 'bg-green-50 text-green-800 hover:bg-green-100 border border-green-200'
                                }`}
                            >
                                {statusMutation.isPending ? '…' : nextStatus === 'Suspended' ? 'Suspend' : 'Activate'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Tab strip — each tab is a nested child route rendered into the Outlet below */}
            <nav className="flex gap-1 overflow-x-auto whitespace-nowrap border-b border-neutral-200 pb-2">
                <Link
                    to="/tenants/$slug"
                    params={{ slug }}
                    activeOptions={{ exact: true }}
                    className={tabLinkCls}
                    activeProps={tabActiveProps}
                    inactiveProps={tabInactiveProps}
                >
                    Overview
                </Link>
                <Link
                    to="/tenants/$slug/ralph"
                    params={{ slug }}
                    className={tabLinkCls}
                    activeProps={tabActiveProps}
                    inactiveProps={tabInactiveProps}
                >
                    Ralph
                </Link>
                <Link
                    to="/tenants/$slug/companies"
                    params={{ slug }}
                    className={tabLinkCls}
                    activeProps={tabActiveProps}
                    inactiveProps={tabInactiveProps}
                >
                    Companies
                </Link>
                <Link
                    to="/tenants/$slug/modules"
                    params={{ slug }}
                    className={tabLinkCls}
                    activeProps={tabActiveProps}
                    inactiveProps={tabInactiveProps}
                >
                    Modules
                </Link>
                <Link
                    to="/tenants/$slug/identity"
                    params={{ slug }}
                    className={tabLinkCls}
                    activeProps={tabActiveProps}
                    inactiveProps={tabInactiveProps}
                >
                    Identity
                </Link>
                <Link
                    to="/tenants/$slug/diagnostics"
                    params={{ slug }}
                    className={tabLinkCls}
                    activeProps={tabActiveProps}
                    inactiveProps={tabInactiveProps}
                >
                    Diagnostics
                </Link>
                <Link
                    to="/tenants/$slug/lifecycle"
                    params={{ slug }}
                    className={tabLinkCls}
                    activeProps={tabActiveProps}
                    inactiveProps={tabInactiveProps}
                >
                    Lifecycle
                </Link>
            </nav>

            <Outlet />
        </div>
    )
}
